/**
 * Customer Engine - Vuex Store Configuration
 * 
 * Centralized state management using a modular Vuex architecture.
 * Each module manages a specific domain of the application state.
 * 
 * Store Architecture:
 * - Modular design with namespaced modules
 * - Domain-driven separation (auth, contacts, campaigns, etc.)
 * - Consistent patterns across all modules
 * - Real-time state synchronization capabilities
 */

import Vue from 'vue'
import Vuex from 'vuex'

// Authentication & User Management
import authModule from './modules/auth-module'
import userModule from './modules/user-module'
import adminModule from './modules/admin-module'
import agencyModule from './modules/agency-module'
import businessModule from './modules/business-module'

// Customer Engagement & Marketing
import automationModule from './modules/automation-module'
import broadcastModule from './modules/broadcast-module'
import couponModule from './modules/coupon-module'
import contactModule from './modules/contact-module'
import landingModule from './modules/landing-module'
import livechatModule from './modules/livechat-module'

// Platform Features
import appModule from './modules/app-module'
import subscriptionModule from './modules/subscription-module'
import paymentMethodModule from './modules/payment-method-module'
import whitelabelModule from './modules/whitelabel-module'
import reportModule from './modules/report-module'
import keywordModule from './modules/keyword-module'
import loopModule from './modules/loop-module'
import whitelistModule from './modules/whitelist-module'
import blacklistModule from './modules/blacklist-module'
import uploadModule from './modules/upload-module'
import integrationModule from './modules/integration-module'
import apikeyModule from './modules/apikey-module'
import customFieldModule from './modules/customfield-module'

Vue.use(Vuex)

/**
 * Main Vuex Store Instance
 * 
 * Combines all feature modules into a single store with strict mode disabled
 * for performance optimization in production environments.
 */
const store = new Vuex.Store({
  strict: false, // Disabled for performance in production
  
  modules: {
    // Core application state
    app: appModule,
    auth: authModule,
    user: userModule,
    
    // User role modules
    admin: adminModule,
    agency: agencyModule,
    business: businessModule,
    
    // Marketing & customer engagement
    automation: automationModule,
    broadcast: broadcastModule,
    coupon: couponModule,
    contact: contactModule,
    landing: landingModule,
    livechat: livechatModule,
    loop: loopModule,
    
    // Platform management
    subscription: subscriptionModule,
    paymentMethod: paymentMethodModule,
    whitelabel: whitelabelModule,
    report: reportModule,
    keyword: keywordModule,
    
    // Utility modules
    whitelist: whitelistModule,
    blacklist: blacklistModule,
    upload: uploadModule,
    integration: integrationModule,
    apikey: apikeyModule,
    customField: customFieldModule,
  },
})

export default store
